import { notifyError } from "@/lib/notify-error";
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { toast } from "sonner";
import {
  calibrateFromPoints,
  calibrateFromPageSizeAndScale,
  calculatePixelDistance,
  detectPageSize,
  parseFeetInchesStrict,
  type Point,
  type PresetScale,
} from "@/lib/geometry";

export type CalibrationState =
  | { phase: "idle" }
  | { phase: "first_point_set"; point1: Point }
  | { phase: "second_point_set"; point1: Point; point2: Point; pixelDistance: number; orientation: "horizontal" | "vertical" }
  | { phase: "calibrated"; pixelsPerFoot: number; presetValue?: string };

export interface MeasurementScale {
  _id: Id<"measurementScales">;
  scaleType: "preset" | "custom";
  presetValue?: string;
  point1X?: number;
  point1Y?: number;
  point2X?: number;
  point2Y?: number;
  pixelDistance?: number;
  realWorldDistance?: number;
  pixelsPerFoot: number;
  pageAspectRatio?: number;
}

interface UseMeasurementScaleOptions {
  scopeKey?: string;
  projectId?: Id<"projects">;
  documentId?: Id<"projectDocuments">;
  revisionId?: Id<"projectDocumentRevisions">;
  pageNumber: number;
  pageAspectRatio?: number;
}

export function useMeasurementScale({
  scopeKey,
  projectId,
  documentId,
  revisionId,
  pageNumber,
  pageAspectRatio,
}: UseMeasurementScaleOptions) {
  const [calibrationState, setCalibrationState] = useState<CalibrationState>({
    phase: "idle",
  });
  
  // Track when user explicitly enters calibration mode (clicks "Set scale")
  const [isCalibrationMode, setIsCalibrationMode] = useState(false);

  const [customDistanceInput, setCustomDistanceInput] = useState("");
  const generation = useRef(0);
  useLayoutEffect(() => {
    generation.current += 1;
    setIsCalibrationMode(false);
    setCalibrationState({ phase: "idle" });
    setCustomDistanceInput("");
    return () => { generation.current += 1; };
  }, [projectId, documentId, revisionId, pageNumber, scopeKey]);

  const scaleQuery = useQuery(
    convexQuery(
      api.measurementScales.getByRevisionPage,
      revisionId ? { revisionId, pageNumber } : "skip"
    )
  );

  const setCustomScale = useMutation(api.measurementScales.setCustomScale);
  const setPresetScale = useMutation(api.measurementScales.setPresetScale);

  const scale: MeasurementScale | null = useMemo(() => {
    if (!scaleQuery.data) return null;
    return {
      _id: scaleQuery.data._id,
      scaleType: scaleQuery.data.scaleType,
      presetValue: scaleQuery.data.presetValue,
      point1X: scaleQuery.data.point1X,
      point1Y: scaleQuery.data.point1Y,
      point2X: scaleQuery.data.point2X,
      point2Y: scaleQuery.data.point2Y,
      pixelDistance: scaleQuery.data.pixelDistance,
      realWorldDistance: scaleQuery.data.realWorldDistance,
      pixelsPerFoot: scaleQuery.data.pixelsPerFoot,
      pageAspectRatio:
        scaleQuery.data.pageAspectRatio ??
        (Number.isFinite(pageAspectRatio) && (pageAspectRatio as number) > 0
          ? (pageAspectRatio as number)
          : undefined),
    };
  }, [pageAspectRatio, scaleQuery.data]);

  const startCalibration = useCallback(() => {
    generation.current += 1;
    setIsCalibrationMode(true);
    setCalibrationState({ phase: "idle" });
    setCustomDistanceInput("");
  }, []);

  /**
   * Cancels the current calibration session, exiting calibration mode
   * and resetting all calibration state.
   */
  const cancelCalibration = useCallback(() => {
    generation.current += 1;
    setIsCalibrationMode(false);
    setCalibrationState({ phase: "idle" });
    setCustomDistanceInput("");
  }, []);

  /**
   * Clears the current calibration. Currently aliases cancelCalibration.
   * @todo In the future, this may also delete the persisted scale from the database
   * when clearing a saved scale vs just canceling an in-progress calibration.
   */
  const clearScale = cancelCalibration;

  const applyCustomDistance = useCallback(async () => {
    if (calibrationState.phase !== "second_point_set") return;
    if (!projectId || !documentId || !revisionId) {
      toast.error("Cannot save scale: missing document context");
      return;
    }

    const parsed = parseFeetInchesStrict(customDistanceInput);
    if (!parsed) {
      toast.error("Invalid distance format. Use format like 10'-6\" where inches must be 0-11");
      return;
    }

    const distance = parsed.totalFeet;
    const resolvedPageAspectRatio =
      Number.isFinite(pageAspectRatio) && (pageAspectRatio as number) > 0
        ? (pageAspectRatio as number)
        : 1;

    // TypeScript knows calibrationState is second_point_set here, no need for type assertion
    const { pixelsPerFoot } = calibrateFromPoints(
      calibrationState.point1,
      calibrationState.point2,
      distance,
      resolvedPageAspectRatio
    );

    const startedAt = generation.current;
    try {
      await setCustomScale({
        projectId,
        documentId,
        revisionId,
        pageNumber,
        point1X: calibrationState.point1.x,
        point1Y: calibrationState.point1.y,
        point2X: calibrationState.point2.x,
        point2Y: calibrationState.point2.y,
        pixelDistance: calibrationState.pixelDistance,
        realWorldDistance: distance,
        pixelsPerFoot,
        pageAspectRatio: resolvedPageAspectRatio,
      });
      if (generation.current !== startedAt) return;
      setIsCalibrationMode(false);
      setCalibrationState({ phase: "calibrated", pixelsPerFoot });
      toast.success(`Scale calibrated: ${distance} feet`);
    } catch (error) {
      if (generation.current !== startedAt) return;
      console.error(error);
      notifyError(error, "Failed to save scale");
    }
  }, [
    calibrationState,
    customDistanceInput,
    pageAspectRatio,
    projectId,
    documentId,
    revisionId,
    pageNumber,
    setCustomScale,
  ]);

  const applyPresetScale = useCallback(
    async (
      pageWidthInches: number,
      preset: PresetScale,
      pageAspectRatioOverride?: number
    ) => {
      if (!projectId || !documentId || !revisionId) {
        toast.error("Cannot save scale: missing document context");
        return;
      }
      const { pixelsPerFoot } = calibrateFromPageSizeAndScale(
        pageWidthInches,
        preset
      );
      const resolvedPageAspectRatio =
        Number.isFinite(pageAspectRatioOverride) &&
        (pageAspectRatioOverride as number) > 0
          ? (pageAspectRatioOverride as number)
          : Number.isFinite(pageAspectRatio) && (pageAspectRatio as number) > 0
            ? (pageAspectRatio as number)
          : 1;

      const startedAt = generation.current;
      try {
        await setPresetScale({
          projectId,
          documentId,
          revisionId,
          pageNumber,
          presetValue: preset.ratio,
          pixelsPerFoot,
          pageAspectRatio: resolvedPageAspectRatio,
        });
        if (generation.current !== startedAt) return;
        setIsCalibrationMode(false);
        setCalibrationState({ phase: "calibrated", pixelsPerFoot, presetValue: preset.ratio });
        toast.success(`Scale set: ${preset.label}`);
      } catch (error) {
        if (generation.current !== startedAt) return;
        console.error(error);
        notifyError(error, "Failed to save scale");
      }
    },
    [projectId, documentId, pageAspectRatio, revisionId, pageNumber, setPresetScale]
  );

  const handleViewportClick = useCallback(
    (point: Point) => {
      if (calibrationState.phase === "idle" || calibrationState.phase === "calibrated") {
        setCalibrationState({ phase: "first_point_set", point1: point });
      } else if (calibrationState.phase === "first_point_set") {
        // Constrain to horizontal or vertical based on which axis has larger movement
        const dx = Math.abs(point.x - calibrationState.point1.x);
        const dy = Math.abs(point.y - calibrationState.point1.y);
        
        const orientation = dx >= dy ? "horizontal" : "vertical";
        let constrainedPoint: Point;
        if (orientation === "horizontal") {
          // Horizontal line - keep y same as point1
          constrainedPoint = { x: point.x, y: calibrationState.point1.y };
        } else {
          // Vertical line - keep x same as point1
          constrainedPoint = { x: calibrationState.point1.x, y: point.y };
        }
        
        const pixelDistance = calculatePixelDistance(calibrationState.point1, constrainedPoint);
        setCalibrationState({
          phase: "second_point_set",
          point1: calibrationState.point1,
          point2: constrainedPoint,
          pixelDistance,
          orientation,
        });
      }
    },
    [calibrationState]
  );

  const updatePoint1 = useCallback((point: Point) => {
    if (calibrationState.phase !== "second_point_set") return;

    // Use stored orientation to maintain constraint
    const constrainedPoint: Point =
      calibrationState.orientation === "horizontal"
        ? { x: point.x, y: calibrationState.point2.y }
        : { x: calibrationState.point2.x, y: point.y };

    const pixelDistance = calculatePixelDistance(constrainedPoint, calibrationState.point2);
    setCalibrationState({
      phase: "second_point_set",
      point1: constrainedPoint,
      point2: calibrationState.point2,
      pixelDistance,
      orientation: calibrationState.orientation,
    });
  }, [calibrationState]);

  const updatePoint2 = useCallback((point: Point) => {
    if (calibrationState.phase !== "second_point_set") return;

    // Use stored orientation to maintain constraint
    const constrainedPoint: Point =
      calibrationState.orientation === "horizontal"
        ? { x: point.x, y: calibrationState.point1.y }
        : { x: calibrationState.point1.x, y: point.y };

    const pixelDistance = calculatePixelDistance(calibrationState.point1, constrainedPoint);
    setCalibrationState({
      phase: "second_point_set",
      point1: calibrationState.point1,
      point2: constrainedPoint,
      pixelDistance,
      orientation: calibrationState.orientation,
    });
  }, [calibrationState]);

  return {
    scale,
    calibrationState,
    isCalibrationMode,
    customDistanceInput,
    setCustomDistanceInput,
    startCalibration,
    handleViewportClick,
    updatePoint1,
    updatePoint2,
    applyCustomDistance,
    applyPresetScale,
    detectPageSize,
    cancelCalibration,
    clearScale,
    isLoading: scaleQuery.isPending,
  };
}
